import type { HomeCopy } from '../types/home';

export const homeIt: HomeCopy = {
  hero: {
    eyebrow: 'Per ristoranti, bar e caffè',
    title: {
      line1: 'Il tuo menu,',
      pre: 'su un ',
      highlight: 'codice QR',
      post: ',',
      line3: 'in pochi minuti.',
    },
    subtitle: {
      intro:
        'I clienti scansionano un piccolo cartoncino sul tavolo e il tuo menu si apre sul telefono. ',
      easyToUse: 'Facile da usare',
      comma: ',',
      safeForGuests: 'sicuro per i clienti',
      and: 'e',
      kindToNature: 'gentile con la natura',
      outro: '. Niente app, niente ristampe, nessun problema.',
    },
    primaryCta: 'Inizia gratis - non serve la carta',
    secondaryCta: 'Scopri come funziona',
    checklist: ['Pronto in ~15 minuti', 'Funziona su ogni telefono', 'Piano gratuito incluso'],
  },
  trustStrip: {
    ariaLabel: 'Perché i ristoranti scelgono SafestMenu',
    words: [
      'Facile da usare',
      'Di fiducia per i clienti',
      'Sicuro e igienico',
      'Rispettoso della natura',
      'Nessuna app necessaria',
      'Si parte gratis',
    ],
  },
  why: {
    eyebrow: 'Perché abbandonare la carta',
    heading: 'I menu di carta costano soldi, diffondono germi e finiscono nel cestino.',
    cards: [
      {
        alt: 'Un tavolo di ristorante impeccabile con un piccolo espositore per codice QR al posto dei menu di carta',
        title: 'Un menu che nessun altro ha toccato',
        text: {
          pre: 'Un menu di carta passa per centinaia di mani e non viene quasi mai lavato. Con un codice QR, ogni cliente lo sfoglia dal proprio telefono - il menu più',
          highlight: 'igienico',
          post: 'che ci sia.',
        },
      },
      {
        alt: 'Un ristoratore che aggiorna il menu dal telefono al pass della cucina',
        title: 'Cambia un prezzo in 30 secondi',
        text: {
          pre: 'Nuovo piatto? Il fornitore ha alzato i prezzi? Modificalo dal telefono e ogni tavolo vede subito l’aggiornamento. È davvero',
          highlight: 'facile da usare',
          post: ' - se sai scrivere un messaggio, sai usare SafestMenu.',
        },
      },
      {
        alt: 'Una pila di menu di carta consumati accanto a un unico espositore QR riutilizzabile e una pianta verde',
        title: 'Stampa una volta, mai più',
        text: {
          pre: 'Ogni stagione di menu ristampati e plastificati è denaro e carta nel cestino. Un piccolo cartoncino per tavolo è',
          highlight: 'amico della natura',
          post: '- e del tuo portafoglio.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'Come funziona',
    heading: 'Tre passi. Nessuna competenza tecnica richiesta.',
    steps: [
      {
        title: 'Scrivi il tuo menu',
        text: 'Aggiungi i tuoi piatti e i prezzi. Non trovi le parole giuste? La nostra IA scrive descrizioni invitanti e crea persino le foto per i piatti di cui non ne hai.',
      },
      {
        title: 'Stampa il tuo codice QR',
        text: 'Scarica i cartoncini da tavolo pronti per la stampa in tutti i formati più comuni. Mettili sui tavoli, in vetrina, sul bancone - ovunque i clienti si siedano.',
      },
      {
        title: 'I clienti scansionano e ordinano',
        text: 'Nessuna app da installare. Il menu si apre nel browser del telefono - con foto, informazioni sugli allergeni e nella loro lingua.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'Novità - importa in pochi minuti',
    heading: 'Hai già un menu? Parti da quello.',
    paragraph:
      'Non serve scrivere tutto da zero. Carica un PDF o qualche foto del tuo menu stampato e SafestMenu lo legge - piatti, prezzi, sezioni - poi ti consegna una bozza pulita da controllare e pubblicare. Un menu tipico rientra nei crediti gratuiti che ricevi alla registrazione.',
    steps: [
      {
        title: 'Carica il tuo menu',
        text: 'Trascina un PDF o fino a 40 foto. Vedi subito il costo esatto - 2 crediti a pagina - prima che ti venga addebitato qualsiasi importo.',
      },
      {
        title: 'L’IA legge ogni pagina',
        text: 'Ogni pagina diventa piatti, prezzi e sezioni modificabili, con lingua e valuta del menu rilevate automaticamente.',
      },
      {
        title: 'Controlla e pubblica',
        text: 'Arrivi su una schermata di revisione ordinata, sistemi ciò che serve, poi crei un nuovo menu o aggiungi le pagine a uno che hai già.',
      },
    ],
  },
  productTour: {
    eyebrow: 'Guardalo in azione',
    heading: 'Guarda un menu prendere forma.',
    paragraph:
      'Da una pagina vuota al tavolo, live. Questo è il vero editor - aggiungi un piatto, lascia che l’IA scriva la descrizione, trascina tutto nell’ordine giusto, premi pubblica. Nessuna demo call necessaria.',
  },
  aiStudio: {
    eyebrow: 'IA integrata',
    heading: {
      pre: 'Niente fotografo?',
      highlight: 'Nessun problema.',
    },
    paragraph:
      'Scatta una foto veloce - o anche nessuna - e lascia che SafestMenu generi immagini dei piatti belle e coerenti. Scrive anche le descrizioni invitanti. Il controllo resta tuo: scegli lo scatto che preferisci, lo tieni, fatto.',
    pills: [
      'Descrizioni IA · 1 credito',
      'Foto IA dei piatti · 5 crediti',
      'Traduzioni del menu · 1 credito ciascuna',
      'Scansione menu · 2 crediti/pagina',
      'Suggerimenti IA sugli allergeni · 1 credito',
      'I crediti non scadono mai',
    ],
  },
  features: {
    eyebrow: 'Tutto incluso',
    heading: 'Pensato per i ristoratori, non per i reparti IT.',
    sidebarCaption:
      'Ogni visualizzazione di un piatto viene contata automaticamente - così scopri cosa desiderano i clienti e metti in vetrina i tuoi piatti più amati.',
    items: [
      {
        title: 'I prezzi si aggiornano all’istante',
        text: 'Modifica tutto dal telefono. Ogni codice QR su ogni tavolo mostra sempre il menu più aggiornato.',
      },
      {
        title: 'Foto e descrizioni IA',
        text: 'Niente fotografo? L’IA integrata scrive descrizioni che fanno venire l’acquolina in bocca e genera le foto dei piatti per te.',
      },
      {
        title: 'Tutti i 14 allergeni UE',
        text: 'Segna gli allergeni una volta per piatto. I clienti vedono etichette chiare al tavolo - basta indovinare, basta risposte rischiose.',
      },
      {
        title: 'Sei lingue integrate',
        text: 'Anche i turisti sono clienti. Il tuo menu parla inglese, tedesco, spagnolo, francese, italiano e rumeno.',
      },
      {
        title: 'Traduci il tuo menu con un clic',
        text: 'L’IA traduce ogni piatto nel tono di voce del tuo menu, un credito ciascuno. Se modifichi un piatto in seguito, viene ritradotto solo quello - resta aggiornato per pochi centesimi.',
      },
      {
        title: 'Menu che cambiano in automatico',
        text: 'Gestisci menu separati per colazione, pranzo e cena. Imposta gli orari una volta sola - lo stesso codice QR mostra sempre il menu che stai servendo in quel momento.',
      },
      {
        title: 'Duplica qualsiasi menu',
        text: 'Copia un intero menu con piatti e foto, modifica ciò che è cambiato, pubblica quando sei pronto. Comodo per i menu stagionali e le seconde sedi.',
      },
      {
        title: 'Scopri cosa desiderano i clienti',
        text: 'Statistiche semplici mostrano quali piatti vengono visti di più - così sai cosa mettere nella lavagna delle proposte del giorno.',
      },
      {
        title: 'Il tuo stile, il tuo brand',
        text: 'Scegli colori e caratteri, aggiungi il tuo logo e le foto dei piatti. Il tuo menu somiglia al tuo ristorante, non al nostro.',
      },
      {
        title: 'Parti dal tuo menu cartaceo',
        text: 'Hai già un menu stampato? Carica un PDF o qualche foto e l’app lo trasforma in piatti e prezzi modificabili - un menu tipico rientra nei crediti gratuiti della registrazione.',
      },
      {
        title: 'L’IA suggerisce gli allergeni',
        text: 'Stai modificando un piatto? Lascia che l’IA legga nome e descrizione e proponga i 14 allergeni UE per un credito. Tu controlli e confermi - un punto di partenza, mai la parola definitiva.',
      },
      {
        title: 'I clienti filtrano il menu da soli',
        text: 'Al tavolo, i clienti aprono un filtro, scelgono cosa evitare o la dieta che serve loro, e i piatti non adatti si fanno da parte. Integrato, sempre attivo, niente da configurare.',
      },
      {
        title: 'Trasforma i clienti in recensioni Google',
        text: 'Aggiungi il link per le recensioni Google e il tuo menu si chiude con un invito gentile - i clienti soddisfatti toccano il link e lasciano una recensione direttamente al tavolo, col pasto ancora fresco.',
      },
    ],
  },
  guests: {
    eyebrow: 'Ai clienti piace',
    heading: {
      pre: 'Un menu di cui i tuoi clienti possono davvero',
      highlight: 'fidarsi',
      post: '.',
    },
    paragraph:
      'Foto chiare, prezzi onesti, allergeni segnati su ogni piatto e l’intero menu nella loro lingua. I clienti ordinano con fiducia - e i clienti fiduciosi ordinano di più.',
    checklist: [
      'Si apre nel browser - niente da installare',
      'Etichette allergeni su ogni singolo piatto',
      'Un tocco traduce l’intero menu nella loro lingua',
    ],
  },
  pricing: {
    eyebrow: 'Prezzi',
    heading: 'Inizia gratis. Resta gratis se ti basta.',
    paragraph:
      'Il tuo primo menu con cinque piatti è gratis - senza carta di credito. Quando il tuo menu cresce, passa a piatti illimitati e più crediti IA, per descrizioni, foto e traduzioni del menu. Tutte e sei le lingue sono incluse in ogni piano. Annulla quando vuoi.',
    price: '€0',
    priceCaption: 'per iniziare',
    cta: 'Crea il tuo menu gratuito',
    disclaimer: 'Nessuna carta di credito. Nessun limite di tempo.',
  },
  faq: {
    eyebrow: 'Domande frequenti',
    heading: 'Le domande dei ristoratori su SafestMenu.',
    items: [
      {
        question: 'Che cos’è SafestMenu e qual è il suo sito ufficiale?',
        answer:
          'SafestMenu è una piattaforma di menu con codice QR per ristoranti, bar e caffetterie. Il sito ufficiale è safestmenu.com e i ristoratori gestiscono i propri menu su app.safestmenu.com.',
      },
      {
        question: 'Come funziona un menu con codice QR di SafestMenu?',
        answer:
          'Crei e pubblichi un menu digitale, quindi stampi il suo codice QR per i tavoli. I clienti lo scansionano con la fotocamera del telefono e il menu aggiornato si apre nel browser.',
      },
      {
        question: 'I clienti devono installare un’app?',
        answer:
          'No. I clienti aprono il menu nel browser del telefono, senza scaricare applicazioni né creare un account.',
      },
      {
        question: 'Posso cambiare piatti e prezzi dopo la pubblicazione?',
        answer:
          'Sì. Modifica il menu dal telefono o dal computer e pubblica l’aggiornamento. Lo stesso codice QR stampato mostrerà la versione più recente.',
      },
      {
        question: 'Posso indicare gli allergeni e offrire il menu in più lingue?',
        answer:
          'Sì. Puoi contrassegnare i piatti con i 14 allergeni UE, permettere ai clienti di filtrare i piatti e offrire il menu in inglese, tedesco, spagnolo, francese, italiano e rumeno.',
      },
      {
        question: 'Posso iniziare gratis con un menu con codice QR?',
        answer:
          'Sì. Il piano gratuito include un menu con un massimo di cinque piatti, non richiede una carta di credito e non ha limiti di tempo.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Provalo subito -',
      highlight: 'scansiona questo codice.',
    },
    paragraph:
      'Questo è esattamente ciò che faranno i tuoi clienti al tavolo. Se ti piace quello che vedi, il tuo menu è a circa quindici minuti da qui.',
    cta: 'Crea il tuo menu oggi',
  },
  jsonLd: {
    appDescription:
      'Menu con codice QR per ristoranti, bar e caffetterie. Crea un menu digitale in pochi minuti o importa il tuo menu esistente da un PDF o da foto, aggiorna i prezzi all’istante, cambia menu in base all’orario, segna i piatti non disponibili, con etichette allergeni, un filtro allergeni per i clienti e traduzione del menu in sei lingue.',
  },
};
